import axios, { AxiosError } from 'axios';
import moment from 'moment';
import jsFileDownload from 'js-file-download';
import _ from 'lodash';
import { ICorpStation, IMap, IUser, UnitedLocationParams, UnitedTruckCodeParams } from './globals/interfaces';
import { renderToString } from 'react-dom/server';
import dayjs from 'dayjs';
import { socket } from './context/socket';
import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import apiClient, { apiClientEosNative } from './apiClient';
import * as yup from 'yup';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';
import QRCodeLib from 'qrcode';
import useSWR from 'swr';

const preventUpperCase = (key: string) => {
    const dontUppercase = [
        's_transaction_id',
        's_mawb_id',
        's_guid',
        's_company_guid',
        's_dock_door_guid',
        's_warehouse_productivity_guid',
        's_user_id',
        'sm_driver_photo',
        's_driver_photo_link',
        's_file_name',
        's_wiki',
    ];
    return (
        dontUppercase.includes(key) ||
        key.includes('guid') ||
        key.includes('url') ||
        key.includes('logo') ||
        key.includes('s_wiki')
    );
};

export const addLocalValue = (
    data: Array<any>,
    setData: React.Dispatch<React.SetStateAction<Array<any>>>,
    value: any,
    prepend?: boolean
) => {
    const copy = _.cloneDeep(data);
    if (prepend) {
        copy.unshift(value);
    } else {
        copy.push(value);
    }
    setData(copy);
};

export const addLocalValues = (
    data: Array<any>,
    setData: (dataSet: Array<any>) => void,
    values: Array<any>
) => {
    const copy = _.cloneDeep(data);
    values.map((v) => copy.push(v));
    setData(copy);
};

export const prependLocalValue = (
    data: Array<any>,
    setData: (dataSet: Array<any>) => void,
    value: any
) => {
    const copy = _.cloneDeep(data);
    copy.unshift(value);
    setData(copy);
};

export const updateLocalValue = (
    dataSet: Array<any>,
    setDataSet: (dataSet: Array<any>) => void,
    id: string | number,
    updateValuesOjb: any,
    alternativeId?: string
) => {
    const useId = alternativeId ? alternativeId : 'id';

    const copy = _.cloneDeep(dataSet);
    const updateIndex = copy.findIndex((item) => item[useId] === id);
    for (let key in updateValuesOjb) {
        let value = updateValuesOjb[key];
        if (value) {
            if (typeof value !== 'boolean' && typeof value !== 'object') {
                if (value.length > 0) {
                    if (!preventUpperCase(key)) {
                        value = value.toUpperCase();
                    }
                }
            }
        }
        copy[updateIndex][key] = value;
    }
    setDataSet(copy);
};

export const deleteLocalValue = (
    dataSet: Array<any>,
    setDataSet: (dataSet: Array<any>) => void,
    id: number | string,
    customId?: string
) => {
    const newSet = dataSet.filter((d) => d[customId || 'id'] !== id);
    const copy = _.cloneDeep(newSet);
    setDataSet(copy);
};

export const asyncHandler = (cb: any) => {
    return async (...args: any) => {
        try {
            return await cb(...args);
        } catch (err: any) {
            if (
                err &&
                err.response &&
                err.response.data &&
                err.response.data.message
            ) {
                alert(
                    `${err.response.status} Error: ${err.response.data.message}`
                );
            }
            if (_.get(err, 'response.data.error', null)) {
                alert(err.response.data.error);
            } else {
                alert(err);
            }
            return {};
        }
    };
};

export const formatCost = (cost: number | string, reverse = false) => {
    const negative = parseFloat(String(cost)) < 0;
    const toFormat =
        cost && !isNaN(parseFloat(String(cost))) ? parseFloat(String(cost)) : 0;
    const formatted = Math.abs(toFormat)
        .toFixed(2)
        .replace(/\d(?=(\d{3})+\.)/g, '$&,');

    if (reverse) {
        return negative ? `$${formatted}` : `($${formatted})`;
    } else {
        return negative ? `($${formatted})` : `$${formatted}`;
    }
};

export const formatMawb = (mawb: string) => {
    return (
        mawb &&
        mawb
            .replace('-', '')
            .substr(0, 13)
            .replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
    );
};

export const mawbFormatter = (mawb: string) => {
    return mawb.substring(0, 13).replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
};

export const validateAwb = (awb: string) => {

    if (_.get(awb, 'length', 0) >= 11) {
        if (awb.substring(0, 3) === '000') return true;

        const normalized = awb.replace(/-/g, '');
        const serial = Number(normalized.substring(3, 10));
        const checkDigit = serial % 7;
        return checkDigit === Number(normalized[normalized.length - 1]);
    } else {
        return false;
    }
};

export const isChoiceEmail = (email: string) => {
    const domain = email && email.split('@')[1];
    return (domain && domain.toUpperCase()) === 'CHOICE.AERO';
};

export const formatEmail = (email: string) => {
    if (email) {
        const parts = email.split('@');
        if (parts.length > 0) {
            return parts[0];
        } else {
            return '';
        }
    } else {
        return '';
    }
};

export const validateEmail = (email: string) => {
    const match = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return match.test(String(email).toLowerCase());
}

export const formatPercent = (float: number, decimal = false) => {
    const percent = float * 100;
    if (decimal && percent < 100) {
        return isNaN(percent) ? '0%' : `${percent.toFixed(1)}%`;
    }
    return isNaN(percent) ? '0%' : `${percent.toFixed(0)}%`;
};

export const getNum = (num: any) => {
    const parsed = parseFloat(num);
    if (!isNaN(parsed)) {
        return Number(parsed.toFixed(2));
    }
    return 0;
};

export const wait = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

export interface SchemaType {
    key: string;
    name: string;
    mawb?: boolean;
    date?: boolean;
    datetime?: boolean;
    time?: boolean;
    percent?: boolean;
}

export const extractReactTableData = (reactTableRef: any, deleteValues: Array<string> = [], schema?: Array<SchemaType>) => {
    if (!reactTableRef || !reactTableRef.current) return;
    // @ts-ignore
    const rawData = reactTableRef.current.getResolvedState().sortedData;
    const data = [];
    for (const { _original } of rawData) {
        for (const str of deleteValues) {
            delete _original[str];
        }
        if (schema && schema.length) {
            const customized: any = {};
            for (const { key, name, mawb, datetime, date, time, percent } of schema) {
                if (mawb) {
                    customized[name] = formatMawb(_original[key]);
                } else if (datetime) {
                    customized[name] = formatDatetime(_original[key]);
                } else if (date) {
                    customized[name] = formatDatetime(_original[key], true);
                } else if (time) {
                    customized[name] = formatDatetime(_original[key], false, true);
                } else if (percent) {
                    customized[name] = formatPercent(_original[key]);
                } else {
                    customized[name] = _original[key];
                }
            }
            data.push(customized);
        } else {
            data.push(_original);
        }
    }
    return data;
}

export const fileDownload = (data: any, name: string, timestamp?: boolean) => {
    const Json2csvParser = require('json2csv').Parser;
    const jsonData = JSON.parse(JSON.stringify(data));
    const parser = new Json2csvParser({ excelStrings: true, withBOM: true });
    const csv = parser.parse(jsonData);
    jsFileDownload(csv, `${name} ${timestamp ? moment().format('YYYY-MM-DD HH-mm') : ''}.csv`);
};

export const downloadPDF = async (pdfLink: string, name: string) => {
    // Fetch the PDF
    const response = await fetch(pdfLink);
    const blob = await response.blob();

    // Create a blob link for download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'document.pdf'; // Name the file as you want
    a.click(); // Trigger the download

    // Cleanup
    window.URL.revokeObjectURL(url);
};

export const getRandomColor = () => {
    var letters = '0123456789ABCDEF';
    var color = '#';
    for (var i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};

export const validateSchema = async (dataSchema: any, data: any) => {
    let validData = false;

    try {
        await dataSchema.validate(data);
        validData = true;
    } catch (err) {
        alert(err);
    }

    return validData;
};

export const objectSorter = (
    property: string,
    sortOrder: Array<any>,
    array: Array<any>
) => {
    const ordering: IMap<any> = {};
    for (let i = 0; i < sortOrder.length; i++) {
        ordering[sortOrder[i]] = i;
    }
    return array.sort((a, b) => ordering[a[property]] - ordering[b[property]]);
};

const getTimeDiff = (start: string) => {
    // console.log(`Start time: ${start}`);

    if (!moment(start, moment.ISO_8601).isValid()) {
        throw new Error(`Invalid date string: ${start}`);
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = moment().tz(timezone);
    const startMoment = moment.utc(start).tz(timezone, true);

    // console.log(`Time now in local timezone: ${now}`);
    // console.log(`Start time in local timezone: ${startMoment}`);

    const diff = moment.duration(now.diff(startMoment));
    return diff;
}

export const timeSince = (start: string) => {
    const diff = getTimeDiff(start);
    let days = parseInt(String(diff.asDays())); //84
    let hours = parseInt(String(diff.asHours())); //2039 hours, but it gives total hours in given miliseconds which is not expacted.
    hours = hours - days * 24; // 23 hours
    let minutes = parseInt(String(diff.asMinutes())); //122360 minutes,but it gives total minutes in given miliseconds which is not expacted.
    minutes = minutes - (days * 24 * 60 + hours * 60);
    minutes = Math.max(0, minutes);
    const stringMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${hours}:${stringMinutes}`;
};

export const getMinutes = (start: string) => {
    const diff = getTimeDiff(start);
    const minutes = diff.asMinutes();
    // console.log(`Minutes difference is: ${minutes}`);

    return minutes;
}

export const formatSecondsAsMinutes = (input: number) => {
    if (!input || isNaN(input)) return '';

    var sec_num = parseInt(String(input), 10); // don't forget the second param
    let hours = Math.floor(sec_num / 3600);
    let minutes = Math.floor((sec_num - (hours * 3600)) / 60);
    // @ts-ignore
    let seconds = sec_num - (hours * 3600) - (minutes * 60);

    // @ts-ignore
    if (hours < 10) { hours = "0" + hours; }
    // @ts-ignore
    if (minutes < 10) { minutes = "0" + minutes; }
    // @ts-ignore
    if (seconds < 10) { seconds = "0" + seconds; }
    return hours + ':' + minutes + ':' + seconds;
}

export const formatMinutes = (minutes: number) => {
    const hours = Math.floor(Math.round(minutes) / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${Math.round(mins).toString().padStart(2, '0')}`;
}

export interface IUnitMap {
    color: string;
    array: Array<string>;
}

export const getUnitsMap = (units: Array<string> = []) => {
    const map: IMap<IUnitMap> = {};
    units.sort((a, b) => a.localeCompare(b));
    for (let i = 0; i < units.length; i++) {
        const start = units[i][0];
        if (map[start] === undefined) {
            const saturation = 43 + (i * 5);
            map[start] = {
                color: `hsl(139deg 52% ${saturation}%)`,
                array: [],
            };
        }
        map[start].array.push(units[i]);
    }
    return map;
};

interface IGetStorageDatesReturn {
    d_storage_first_free: string;
    d_storage_second_free: string;
    d_storage_start: string;
}

export const getStorageDates = async (
    d_arrival_date: string,
    s_airline_code: string,
    s_pou: string,
    s_airline_prefix: string,
    s_unit?: string
): Promise<IGetStorageDatesReturn> => {
    const res = await apiClient.post(
        `/storage-dates`,
        {
            d_arrival_date,
            s_airline_code,
            s_pou,
            s_airline_prefix,
            s_unit
        }
    );
    return res.data;
};

export const print = (element: React.ReactNode) => {
    const sheet = renderToString(element);

    const myWindow = window.open("", "MsgWindow", "width=1920,height=1080");
    if (myWindow) {
        myWindow.document.body.innerHTML = null;
        myWindow.document.write(sheet);
    }

}

export const formatTime = (date: string) => {
    return dayjs.utc(date).format('HH:mm');
};


export const formatDatetime = (datetime: string | Date, date?: boolean, time?: boolean) => {
    if (!datetime) return '';

    if (typeof datetime !== 'string') {
        datetime = String(datetime);
    }

    if (datetime && dayjs(datetime).isValid()) {
        if (date) return dayjs.utc(datetime).format('MM/DD/YYYY');
        if (time) return formatTime(datetime);
        return dayjs.utc(datetime).format('MM/DD/YYYY HH:mm');
    }

    return '';
};

export const getDate = () => dayjs().local().format('MM/DD/YYYY HH:mm');
export const getTsDate = () => {
    const now = new Date();
    const date = new Date(+now - now.getTimezoneOffset() * 60 * 1000);
    return date;
};

export const getEpochDate = (dateString: string) => {
    if (!dateString) return null;
    const epoch = new Date(dateString).getTime();
    return epoch;
}


export const formatDecimal = (number: string | number | null) => {
    if (number === null) {
        return '';
    }
    return Number(number).toFixed(1);
};

export const createdUpdatedInfo = (item: any, noPrefix = false) => {
    if (item) {
        let createdBy, createdAt, modifiedBy, modifiedAt;
        if (noPrefix) {
            createdBy = item.createdBy || item.created_by;
            createdAt = item.createdAt || item.created;
            modifiedBy = item.modifiedBy || item.modified_by;
            modifiedAt = item.modifiedAt || item.modified;
        } else {
            const { s_created_by, t_created, s_modified_by, t_modified } = item;
            createdBy = s_created_by;
            createdAt = t_created;
            modifiedBy = s_modified_by;
            modifiedAt = t_modified;
        }

        let base = '';
        if (createdBy && createdAt) {
            base += `Created by ${formatEmail(createdBy)} on ${formatDatetime(createdAt)}`;
        }
        if (modifiedBy && modifiedAt) {
            base += ` and modified by ${formatEmail(modifiedBy)} on ${formatDatetime(modifiedAt)}.`;
        }

        return base;
    }
}

export const validateUrl = (checkUrl: string) => {
    const urlRegex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;;
    const url = new RegExp(urlRegex);
    return checkUrl.length < 2083 && url.test(checkUrl);
}

export const countHoliday = async (d_arrival_date: string) => {
    if (!dayjs(d_arrival_date).isValid()) {
        return false;
    }

    try {
        const res = await apiClient.get(`/count-holiday/${d_arrival_date}`);
        return res.data;
    } catch (err) {
        return false;
    }
}

export const rackUpdate = (s_mawb: string, s_unit: string) => {
    socket.emit('rackUpdate', { s_mawb, s_unit });
}

export const getStationDetails = (user: IUser, stations: Array<ICorpStation>) => {
    return stations.find(s => s.s_unit === user.s_unit);
}

export const notify = (message: string, type: 'info' | 'success' | 'warn' | 'error' = 'success') => {
    if (type === 'error') {
        toast.error(message, {
            autoClose: 3000,
            theme: 'colored',
            position: 'bottom-right',

        });
    } else {
        toast[type](message, {
            position: 'top-center',
            autoClose: 3000,
            theme: 'colored'
        });
    }
}

export const checkPayment = (s_payment_method: string) => {
    return !['CHARGE', 'OVERRIDE'].includes(s_payment_method);
}

export function omit<T, K extends keyof any>(obj: T, key: K): Omit<T, K> {
    const { [key]: _, ...rest } = obj as any;
    return rest;
}

export async function compressImageFromBase64(base64Str: string): Promise<string> {
    // Convert base64 to Blob
    const byteString = atob(base64Str.split(',')[1]);
    const mimeString = base64Str.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    const imgBlob = new Blob([ab], { type: mimeString });

    const options = {
        maxSizeMB: 1,             // (default: Number.POSITIVE_INFINITY)
        maxWidthOrHeight: 1920,   // (default: undefined)
        useWebWorker: true,       // (default: true)
        maxIteration: 10,         // (default: 10)
    };

    try {
        // @ts-ignore
        const compressedFile = await imageCompression(imgBlob, options);
        // If you want to return the compressed image in base64 format, do this:
        return await blobToBase64(compressedFile);
        //return compressedFile; // Return compressed Blob
    } catch (error) {
        console.log(error);
        return '';
    }
}

// Helper function to convert Blob to base64 (optional)
export async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = function () {
            resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
export const convertImageToBase64 = async (imageUrl: string) => {
    try {
        const response = await axios.get(imageUrl, {
            responseType: 'arraybuffer'
        });

        // Convert buffer to base64 string
        const base64 = Buffer.from(response.data, 'binary').toString('base64');

        // Check the content type of the image (e.g., jpeg, png)
        const contentType = response.headers['content-type'];

        return base64;
    } catch (error) {
        // console.error("Error fetching and converting image", error);
        return null;
    }
};

export const getUserStation = (stations: Array<ICorpStation>, user: IUser) => {
    const userStation = stations.find((unit: ICorpStation) => unit.s_unit === user.s_unit);
    return userStation;
}

export const getUsername = async (s_email: string) => {
    if (!s_email) {
        return '';
    }

    try {
        const response = await apiClient.get(`/user-data/${s_email}`)
        const { s_first_name, s_last_name } = response.data;
        return `${s_first_name} ${s_last_name}`;
    } catch (error) {
        return '';
        console.log(error);
    }

};

export const handleError = (err: any) => {
    const axiosError = err as any;
    if (axiosError.response) {
        notify(axiosError.message, 'error');
    }
}

export function isValidPhoneNumber(phoneNumber: string) {
    const normalized = phoneNumber.replace(/[()-]/g, '');
    // Regular expression for a 10-digit number with no special characters
    const regex = /^\d{10}$/;

    return regex.test(normalized);
}

export function formatPhoneNumber(phoneNumber: string) {
    const match = phoneNumber.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return `(${match[1]})-${match[2]}-${match[3]}`;
    }
    return null;
}

export const properCaps = (text: string) => {
    if (!text) return '';
    const array = text.split(' ');
    const output = [];
    for (const item of array) {
        if (item && item.length) {
            output.push(`${item[0].toUpperCase()}${item.substring(1).toLowerCase()}`);
        }
    }
    return output.join(' ');
}

export const safeGet = (obj: object, path: string, defaultValue = '') => {
    const result = _.get(obj, path);
    return (result === null || result === undefined) ? defaultValue : result;
};

export const getInitials = (firstName: string, lastName: string) => {
    return `${(firstName || '').substring(0, 1)}${(lastName || '').substring(0, 1)}`;
}

export const getDayName = (dayNumber: number, short?: boolean) => {
    if (dayNumber === null || dayNumber === undefined) return '';
    const name = moment().day(dayNumber).format('dddd');
    return short ? name.substring(0, 3) : name;
}

export const formatBoolean = (condition: boolean) => condition === null ? 'N/A' : condition === true ? 'YES' : 'NO';

export const yupMawbValidator = yup
    .string()
    .required('Please enter a valid AWB')
    .test(
        'valid awb',
        'Invalid AWB number',
        function (value: string) {
            return validateAwb(value);
        }
    );

export const getGuid = () => uuid();

export const resolveQueueCardColors = (s_state: string) => {
    switch (s_state) {
        case 'EXPORT':
            return '#6BB4DD';
        case 'IMPORT':
            return '#61B996';
        case 'MIXED':
            return 'goldenrod';
        case 'TRANSFER-IMPORT':
            return '#BBA0CA';
        default:
            return 'grey';
    }
}

export const extractDate = (date: Date | string): any => {
    return moment(date).isValid() ? date : '';
};

export const diffDays = (from: string, to: string) => {
    const days =
        moment(to, 'YYYY-MM-DD')
            .startOf('day')
            .diff(moment(from, 'YYYY-MM-DD').startOf('day'), 'days') + 1;
    return Math.max(days, 0);
};

export const getFileIcon = (fileName: string) => {
    if (!fileName) return '';
    const parts = fileName.split('.');
    if (!parts || !parts.length) return '';
    const fileType = parts[parts.length - 1];
    const typesMap = {
        'pdf': 'fa-duotone fa-file-pdf',
        'png': 'fa-duotone fa-image',
        'jpg': 'fa-duotone fa-image',
        'jpeg': 'fa-duotone fa-image'
    }
    return typesMap[fileType] || 'fa-duotone fa-file';
}

export const compareEmails = (a: string | null, b: string | null) => {
    if (!a || !b) return false;
    return a.toUpperCase() === b.toUpperCase();
};

export const unitedApiCheck = (user: IUser) => ['CEWR2', 'CEWR3', 'TBSB1'].includes(user.s_unit) && Boolean(user.additionalData.unitedVNumber);

export const getUnitedDate = () => moment().format('YYYY-MM-DDTHH:mm:ss');

export const getDeviceId = () => {
    return `EOS-WEB-${getGuid().substring(0, 8)}`
}

export const checkInternalUser = (email: string) => {
    if (!email) return false;
    const parts = email.split('@');
    if (parts && parts.length === 2 && parts[1].toLowerCase() === 'choice.aero') {
        return true;
    }
    return false;
}


export async function customFetcher<T>(key: string): Promise<Array<T>> {
    try {
        const res = await apiClient.get(key);
        return res.data;
    } catch (err) {
        const axiosErr = err as AxiosError;

        // Handle rate-limit 429 specifically
        if (axiosErr.response?.status === 429) {
            // Show custom message (can replace with toast, logger, etc.)
            notify('Too many requests, please try again in a few minutes', 'warn');
        } else {
            notify(`Error: ${axiosErr.response.status} | ${axiosErr.response.data}`, 'error');
        }

        // Default fallback
        console.error("Fetcher error:", axiosErr.message);
        return [];
    }
}


const staticFetcher = async (endpoint) => {
    const res = await apiClient.get(endpoint);
    return res.data;
};

// Custom hook for static data with long cache
export const useStaticData = <T = any>(endpoint: string, options = {}) => {
    return useSWR<T>(endpoint, staticFetcher, {
        // Cache for 1 hour in memory
        dedupingInterval: 60 * 60 * 1000,

        // Don't refetch unless manually triggered
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: true,

        // Only refresh once per day
        refreshInterval: 24 * 60 * 60 * 1000,

        // Don't retry failed requests immediately
        errorRetryCount: 1,
        errorRetryInterval: 5 * 60 * 1000,

        ...options
    });
};

export async function customFetcherNative<T>(key: string): Promise<Array<T>> {
    try {
        const res = await apiClientEosNative.get(key);
        return res.data;
    } catch (err) {
        return [];
    }
}

export const getQueryString = (queryMap: Record<string, any>) => {
    let i = 0;
    let string = '';
    for (const key in queryMap) {
        if (!queryMap[key]) return undefined;
        string += `${i !== 0 ? '&' : ''}${key}=${queryMap[key]}`;
        i++;
    }
    return string;
}

// Helper function to transform string numbers to actual numbers
export const numericString = (field: string, allowNegative = false, overrideMessage?: string) =>
    z.union([
        z.string().min(1, overrideMessage || `${field} cannot be empty`),
        z.number()
    ])
        .transform(value => {
            if (typeof value === "number") return value;
            const parsed = parseFloat(value);
            return parsed;
        })
        .refine(value => !isNaN(value), `${field} must be a valid number`)
        .refine(value => allowNegative || value >= 0, `${field} cannot be negative`);

export const nullableNumber = z.union([z.number(), z.string().min(1)]).transform(val => {
    if (typeof val === "string") return parseFloat(val);
    return val;
}).refine(val => !isNaN(val), "Must be a valid number").nullable();

export const dateString = z.union([z.string(), z.date()]);

export const optional = <T extends z.ZodTypeAny>(schema: T) =>
    schema.nullable().optional();

export type ZodEntriesOf<T> = { [Key in keyof T]: z.ZodType<any, any, T[Key]> }

export const getQrCodeUrl = (str: string): Promise<string> => new Promise((resolve, reject) => {
    QRCodeLib.toDataURL(str).then((url, err) => {
        resolve(url);
        if (err) {
            reject(err);
        }
    })
});

export const unitedVNumberAlert = () => {
    alert('Error: User is missing United V Number');
}


export const getUnitedBuildingCode = (s_unit: string) => {
    switch (s_unit) {
        case 'CEWR2':
            return '344';
        case 'CEWR3':
            return '100';
        default:
            return 'N/A';
    }
}

export const getUnitedTruckCode = ({
    breakdownAcceptance,
    load,
    dockDoor,
    truckName,
    overrideLocation,
    s_unit
}: UnitedTruckCodeParams): string => {

    if (overrideLocation) {
        return overrideLocation;
    }

    if (breakdownAcceptance) {
        // breakdown acceptance load/unload = accept/reverse
        if (s_unit === 'CEWR2') {
            if (load) {
                return 'CANOPY';
            } else {
                return 'YARD';
            }
        } else if (s_unit === 'CEWR3') {
            if (load) {
                return 'RD';
            } else {
                return 'TRUCK';
            }
        }
    }

    return load ? `${dockDoor}T${truckName}` : 'TFEWR';
}

export const getUnitedLocation = ({ s_unit, breakdownAcceptance, load, dockDoor, truckName, overrideLocation }: UnitedLocationParams): string => {
    const buildingCode = getUnitedBuildingCode(s_unit);
    const truckCode = getUnitedTruckCode({ breakdownAcceptance, load, dockDoor, truckName, overrideLocation, s_unit });
    return `EWR - ${buildingCode} ${truckCode}`;
}

export const safeJSON = (str, fallback = {}) => {
    try { return JSON.parse(str); }
    catch { return fallback; }
};

export function getRandomNum(min, max) {
    const minCeiled = Math.ceil(min);
    const maxFloored = Math.floor(max);
    return Math.floor(Math.random() * (maxFloored - minCeiled + 1) + minCeiled);
}
